import type { DataViewRouteState } from "./viewController";
import type { DataViewState } from "./viewDefinition";
import { parseProjectTaskDataViewSearch, serializeProjectTaskDataViewSearch } from "./viewUrlState";

const PROJECT_TASK_VIEW_PREFERENCE_PREFIX = "forge:cmmd:project-task-view:v1:";
const PROJECT_TASK_VIEW_SEARCH_KEYS: ReadonlySet<string> = new Set([
  "taskSavedView",
  "taskLayout",
  "taskQ",
  "taskStatus",
  "taskOwner",
  "taskMilestone",
  "taskGroup",
  "taskSort",
]);
const PROJECT_TASK_STATE_SEARCH_KEYS: ReadonlySet<string> = new Set(
  [...PROJECT_TASK_VIEW_SEARCH_KEYS].filter((key) => key !== "taskSavedView"),
);

interface StoredProjectTaskViewPreference {
  readonly version: 1;
  readonly search: string;
}

function withExplicitState(
  route: DataViewRouteState,
  search: Record<string, unknown>,
  storedPreference = false,
): DataViewRouteState {
  const parsed = route.state;
  const validTaskStateSearch: Readonly<Record<string, boolean>> = {
    taskLayout:
      typeof search.taskLayout === "string" &&
      ["list", "board", "timeline"].includes(search.taskLayout),
    taskQ: typeof search.taskQ === "string" && search.taskQ.trim().length > 0,
    taskStatus: typeof search.taskStatus === "string" && search.taskStatus.trim().length > 0,
    taskOwner: typeof search.taskOwner === "string" && search.taskOwner.trim().length > 0,
    taskMilestone:
      typeof search.taskMilestone === "string" && search.taskMilestone.trim().length > 0,
    taskGroup:
      typeof search.taskGroup === "string" && ["status", "milestone"].includes(search.taskGroup),
    taskSort: parsed.taskSort != null,
  };
  const hasStateSearch = Object.keys(search).some(
    (key) => PROJECT_TASK_STATE_SEARCH_KEYS.has(key) && validTaskStateSearch[key] === true,
  );
  return {
    ...route,
    hasExplicitState: hasStateSearch || (storedPreference && route.savedViewId.length === 0),
  };
}

function storageKey(projectId: string): string {
  return `${PROJECT_TASK_VIEW_PREFERENCE_PREFIX}${projectId}`;
}

function hasExplicitProjectTaskViewSearch(search: Record<string, unknown>): boolean {
  return Object.keys(search).some((key) => PROJECT_TASK_VIEW_SEARCH_KEYS.has(key));
}

export function shouldPersistProjectTaskViewRoute(route: DataViewRouteState): boolean {
  return route.hasExplicitState === true || route.savedViewId.length > 0;
}

/**
 * Materialize a history entry before leaving a bare task URL. The normal URL
 * serializer omits defaults, but a bare entry would later resolve from the
 * newly updated last-view preference and make Back appear to do nothing.
 */
export function serializeProjectTaskViewHistoryEntry(
  state: DataViewState,
  savedViewId: string | null,
): Record<string, string | undefined> {
  const serialized = serializeProjectTaskDataViewSearch(state, savedViewId);
  const explicit = Object.fromEntries(
    Object.entries(serialized).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );
  return hasExplicitProjectTaskViewSearch(explicit)
    ? serialized
    : { ...serialized, taskLayout: state.layout };
}

function parseStoredPreference(raw: string | null): DataViewRouteState | null {
  if (raw === null) return null;
  try {
    const stored = JSON.parse(raw) as Partial<StoredProjectTaskViewPreference>;
    if (stored.version !== 1 || typeof stored.search !== "string") return null;
    const search = Object.fromEntries(new URLSearchParams(stored.search).entries());
    return withExplicitState(parseProjectTaskDataViewSearch(search), search, true);
  } catch {
    return null;
  }
}

export function resolveProjectTaskViewPreference(
  storage: Storage | null,
  projectId: string,
  search: Record<string, unknown>,
): DataViewRouteState {
  if (hasExplicitProjectTaskViewSearch(search)) {
    const parsed = withExplicitState(parseProjectTaskDataViewSearch(search), search);
    if (parsed.hasExplicitState === true || parsed.savedViewId.length > 0) return parsed;
  }
  if (storage === null) return parseProjectTaskDataViewSearch(search);
  try {
    return (
      parseStoredPreference(storage.getItem(storageKey(projectId))) ??
      parseProjectTaskDataViewSearch(search)
    );
  } catch {
    return parseProjectTaskDataViewSearch(search);
  }
}

export function persistProjectTaskViewPreference(
  storage: Storage | null,
  projectId: string,
  state: DataViewState,
  savedViewId: string | null,
): void {
  if (storage === null) return;
  const serialized = serializeProjectTaskDataViewSearch(state, savedViewId);
  const search = new URLSearchParams(
    Object.entries(serialized).flatMap(([key, value]) =>
      value !== undefined ? [[key, value]] : [],
    ),
  ).toString();
  try {
    storage.setItem(storageKey(projectId), JSON.stringify({ version: 1, search }));
  } catch {
    // Storage can be unavailable in private or embedded contexts. The URL remains authoritative.
  }
}
